// ** Component Imports
import { SubscriptionCard } from './SubscriptionCard'
import {
	PlanCard,
	PlanCardError,
	PlanCardSkeleton
} from '@/components/pages/subscription-plan'

// ** Entity Imports
import { useGetMyPlanQuery } from '@/entities/custom-pricing'

// ** Action Imports
import {
	useGenerateStripeCheckoutSessionUrlMutation,
	useGenerateStripeCustomerPortalUrlMutation
} from '../api/subscription'

// ** Styled Components
import { OptionsWrapper } from './styles'

// ** Lib Imports
import { enterpriseCardFeatures } from '../lib/features'

// ** Type Imports
import { OptionType } from '../model/types'

// A lone card is constrained instead of stretching the two-card layout's width.
const SINGLE_CARD_CLASSES = 'flex w-full max-w-136'

export const SubscriptionOptions = () => {
	// ** Hooks
	// works without an active subscription, so it is safe on the pre-checkout screens
	const {
		data: myPlan,
		isLoading,
		isError,
		isFetching,
		refetch
	} = useGetMyPlanQuery()
	const [generateCheckoutUrl, { isLoading: isCheckoutLoading }] =
		useGenerateStripeCheckoutSessionUrlMutation()
	const [generatePortalUrl, { isLoading: isPortalLoading }] =
		useGenerateStripeCustomerPortalUrlMutation()

	const redirectTo = async (
		generateUrl: typeof generateCheckoutUrl | typeof generatePortalUrl
	) => {
		const result: any = await generateUrl({})

		if (result?.data?.url) {
			window.location.href = result.data.url // redirects to Stripe
		}
	}

	if (isLoading) {
		return (
			<div className={SINGLE_CARD_CLASSES}>
				<PlanCardSkeleton />
			</div>
		)
	}

	// no hardcoded fallback: showing a price the backend did not confirm is worse
	// than showing none
	if (isError || myPlan === undefined) {
		return (
			<div className={SINGLE_CARD_CLASSES}>
				<PlanCardError isRetrying={isFetching} onRetry={refetch} />
			</div>
		)
	}

	const planCard = (
		<PlanCard
			plan={myPlan}
			isRedirecting={isCheckoutLoading || isPortalLoading}
			onSubscribe={async () => {
				await redirectTo(generateCheckoutUrl)
			}}
			onManageBilling={async () => {
				await redirectTo(generatePortalUrl)
			}}
		/>
	)

	// A negotiated plan replaces the comparison table outright: there is nothing
	// to choose between and plan switching is disabled for them.
	if (myPlan.isCustom) {
		return <div className={SINGLE_CARD_CLASSES}>{planCard}</div>
	}

	return (
		<OptionsWrapper>
			{planCard}

			<SubscriptionCard
				type={OptionType.ENTERPRISE}
				title='enterprise'
				description='Designed for firms managing more than 50 clients. Tailored pricing and advanced support.'
				className='enterprise'
				price='Custom pricing'
				features={enterpriseCardFeatures}
				buttonSettings={{ label: 'Contact Sales', variant: 'secondary' }}
			/>
		</OptionsWrapper>
	)
}
